import { documentationClient } from 'shared/domain/documentation/documentationClient';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { CommonTranslationKeys } from 'intl/keys';
import {
  GalleryContextType,
  GalleryState,
} from 'components/common/gallery';
import { GalleryAction } from 'components/common/gallery/galleryAction';
import { FileSetForDisplay } from 'components/common/gallery/filesStore';
import { DrawingType } from 'components/common/graphicUploader/types';
import { useAsyncDialog } from 'components/core/Dialog/common/useAsyncDialog';
import { useDocumentationForm } from 'components/dataCreationForms/withDocumentationForm';
import { useCreateStore } from 'hooks/createStore';
import { getNextQuarterTurnRotation } from 'helpers/image';
import { logError } from 'setup/logger/logError';
import {
  DocumentationOperationType,
} from 'shared/domain/documentation/operationPreflight';
import { DocumentationEditDialog } from 'views/documentations/edit/dialog';
import {
  confirmDocumentationOperation,
  isExecutedOperationResult,
} from 'components/documentation/documentationOperationDialog';
import { isRestoreOperation } from 'shared/domain/documentation/operationTypeGuards';
import { DocumentationMapOverlay } from 'components/documentation/DocumentationMapOverlay';
import { DocumentationMiniaturesOverlay } from 'components/documentation/DocumentationMiniaturesOverlay';
import { DocumentationProgressControls } from 'components/documentation/DocumentationProgress/DocumentationProgressControls';
import { ProgressModel } from 'components/documentation/DocumentationProgress/types';
import { useDocumentationProgressActivityStore } from 'components/documentation/DocumentationProgress/useDocumentationProgressActivityStore';
import { useDocumentationProgressComponent } from 'components/documentation/DocumentationProgress/useDocumentationProgressComponent';
import { isArchived } from 'components/documentation/DocumentationDataGrid/model';
import { useIssueOverlayControls } from 'components/documentation/DocumentationDataGrid/components/IssueOverlayControls';
import { useDocumentationStore } from '../store';
import {
  DocumentationDisplayedFile,
  DocumentationGalleryIds,
} from '../slices/gallery/types';
import { applySignedUrlsToGalleryFile } from '../slices/gallery/model';
import { useAppNavigation } from 'contexts/navigation';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { signalNavigationOutsideDocumentation } from './useDocumentationUrlNavigation';
import {
  setGalleryState,
  useDocumentationDisplayedFiles,
  useDocumentationGalleryState,
  useDocumentationMainDisplayedFile,
} from './documentationGalleryContextHooks';

const drawings: DrawingType[] = [];
const addDrawing = (): void => {};

const documentationGalleryActions = [
  GalleryAction.REMOVE,
  GalleryAction.DOWNLOAD,
  GalleryAction.REFRESH_PREVIEW,
  GalleryAction.EDIT,
  GalleryAction.ROTATE,
  GalleryAction.SHOW_ISSUES,
  GalleryAction.GO_TO_SOURCE_ISSUE,
  GalleryAction.FILTERS,
];

/** Updates the gallery file of a version, if it is still in the gallery. */
function updateGalleryFileOfVersion(
  versionId: string,
  update: (file: DocumentationDisplayedFile) => Partial<DocumentationDisplayedFile>
): void {
  const { files, updateFileAtIndex } = useDocumentationStore.getState();
  const fileIndex = files.findIndex((file) => String(file.versionId ?? '') === versionId);
  if (fileIndex !== -1) {
    updateFileAtIndex(fileIndex, update(files[fileIndex]));
  }
}

export function useDocumentationGalleryStore(): GalleryContextType<DocumentationGalleryIds> {
  const createAsyncDialog = useAsyncDialog();
  const { openDialog } = useDocumentationForm();
  const projectId = useAppUrlParams(UrlParamName.PROJECT_ID);
  const { goToDocumentationFile } = useAppNavigation();

  // No subscriptions to files / slide here: actions read the store on demand,
  // so this hook (and the grid using it) does not re-render on file updates.
  const setFilesInStore = useDocumentationStore.use.setFiles();
  const openGalleryAtSlide = useDocumentationStore.use.openGalleryAtSlide();
  const closeGallery = useDocumentationStore.use.closeGallery();
  const navigateToSlide = useDocumentationStore.use.navigateToSlide();
  const removeFileFromGallery = useDocumentationStore.use.removeFileFromGallery();
  const updateFileAtIndex = useDocumentationStore.use.updateFileAtIndex();
  const prepareOperationByIds = useDocumentationStore.use.prepareOperationByIds();
  const confirmPreparedOperation =
    useDocumentationStore.use.confirmPreparedOperation();
  const clearPreparedOperation =
    useDocumentationStore.use.clearPreparedOperation();

  /** The pending signed URL read of each version; a newer read is never dropped by an older one. */
  const signedUrlReadsRef = useRef<Map<string, Promise<boolean>>>(new Map());

  const changeGallery = useCallback(
    (key: keyof GalleryState, value: GalleryState[keyof GalleryState]) => {
      if (key === 'open') {
        if (value) {
          openGalleryAtSlide(useDocumentationStore.getState().currentSlide);
        } else {
          closeGallery();
        }
      } else if (key === 'slide' && typeof value === 'number') {
        navigateToSlide(value);
      }
    },
    [openGalleryAtSlide, closeGallery, navigateToSlide]
  );

  const setFiles = useCallback(
    (newFiles: FileSetForDisplay<DocumentationGalleryIds>[]) => {
      const filesWithLoading: DocumentationDisplayedFile[] = newFiles.map((file) => ({
        ...file,
        loading: !file.signedRequest,
      }));
      setFilesInStore(filesWithLoading);
    },
    [setFilesInStore]
  );

  /**
   * Reads the signed URLs of a version into its gallery file; resolves whether
   * the version has a converted image. A failed read marks a file that shows
   * nothing yet; a shown preview stays.
   */
  const loadSignedUrls = useCallback(
    (documentationId: string, versionId: string): Promise<boolean> => {
      const fetchKey = `${documentationId}-${versionId}`;
      const read: Promise<boolean> = documentationClient
        .getSignedUrl({ documentationId, versionId })
        .then((urls) => {
          updateGalleryFileOfVersion(versionId, (file) => applySignedUrlsToGalleryFile(file, urls));
          return Boolean(urls.imageSignedRequest);
        })
        .catch(() => {
          updateGalleryFileOfVersion(versionId, (file) =>
            file.signedRequest ? { loading: false } : { loading: false, error: 'fetchFailed' }
          );
          return false;
        })
        .finally(() => {
          if (signedUrlReadsRef.current.get(fetchKey) === read) {
            signedUrlReadsRef.current.delete(fetchKey);
          }
        });
      signedUrlReadsRef.current.set(fetchKey, read);
      return read;
    },
    []
  );

  const downloadFile = useCallback(
    (file: DocumentationDisplayedFile) => {
      if (!file.documentationId || !file.versionId) return;
      if (file.signedRequest) return;
      if (file.error) return;
      if (signedUrlReadsRef.current.has(`${file.documentationId}-${file.versionId}`)) return;

      void loadSignedUrls(file.documentationId, file.versionId);
    },
    [loadSignedUrls]
  );

  const refreshPreview = useCallback((): Promise<boolean> => {
    const currentFile = useDocumentationStore.getState().getCurrentFile();
    if (!currentFile?.documentationId || !currentFile.versionId) {
      return Promise.resolve(false);
    }

    if (currentFile.signedRequest?.imageSignedRequest) {
      return Promise.resolve(true);
    }

    const fileIndex = useDocumentationStore
      .getState()
      .files.findIndex((file) => file.localId === currentFile.localId);

    if (fileIndex >= 0) {
      updateFileAtIndex(fileIndex, { loading: true, error: undefined });
    }

    return loadSignedUrls(currentFile.documentationId, currentFile.versionId);
  }, [loadSignedUrls, updateFileAtIndex]);

  // Fetch the signed URL of the displayed file when it changes (selected store
  // state instead of a render-time subscription to all files).
  useEffect(
    () =>
      useDocumentationStore.subscribe(
        (state) => state.files[state.currentSlide],
        (file) => {
          if (file && !file.signedRequest && !file.error) {
            downloadFile(file);
          }
        },
        { fireImmediately: true }
      ),
    [downloadFile]
  );

  const performGalleryOperation = useCallback(
    async (
      operationType: DocumentationOperationType,
      idx: number,
      localId: number,
      documentationId?: string
    ): Promise<void> => {
      const prepared = await prepareOperationByIds(operationType, {
        documentations: [localId],
        directories: [],
      });

      if (!prepared || prepared.resolvedCount === 0) {
        return;
      }

      const result = await confirmDocumentationOperation({
        prepared,
        createAsyncDialog,
        confirmPreparedOperation,
        getPreparedOperation: () => useDocumentationStore.getState().preparedOperation,
        clearPreparedOperation,
      });
      if (!isExecutedOperationResult(result)) {
        return;
      }

      if (isRestoreOperation(operationType) && projectId && documentationId) {
        // Close gallery and navigate to restored file URL.
        // Suppress gallery-close URL sync so it does not redirect back to directory.
        signalNavigationOutsideDocumentation();
        setGalleryState({ open: false, slide: 0 });
        goToDocumentationFile(projectId, documentationId, {
          replace: true,
        });
        return;
      }
      removeFileFromGallery({ localId, documentationId }, idx);
    },
    [
      clearPreparedOperation,
      confirmPreparedOperation,
      createAsyncDialog,
      goToDocumentationFile,
      prepareOperationByIds,
      projectId,
      removeFileFromGallery,
    ]
  );

  const removeFile = useCallback(
    (idx: number) => {
      const file = useDocumentationStore.getState().files[idx];
      if (!file || file.localId === undefined) return;

      const operationType = isArchived(file)
        ? DocumentationOperationType.restore
        : DocumentationOperationType.archive;
      void performGalleryOperation(operationType, idx, file.localId, file.documentationId);
    },
    [performGalleryOperation]
  );

  const rotateFile = useCallback(() => {
    const { currentSlide, getCurrentFile } = useDocumentationStore.getState();
    const file = getCurrentFile();
    if (!file) return;

    if (file.localId === undefined || !file.versionId) {
      logError(
        'rotateFile: Cannot rotate file - file, localId or versionId is missing.'
      );
      return;
    }

    const imageRotation = getNextQuarterTurnRotation(file.imageRotation);
    documentationClient.editVersion({
      versionId: file.versionId,
      imageRotation,
      localId: file.localId,
    });
    updateFileAtIndex(currentSlide, { imageRotation });
  }, [updateFileAtIndex]);

  const rightPanelVisibleStore = useCreateStore(false);
  const progressModelsStore = useCreateStore<ProgressModel[]>([]);
  const { issuesVisibleStore } = useIssueOverlayControls();
  const activityStore = useDocumentationProgressActivityStore(issuesVisibleStore);
  const documentationProgressControls = useRef(
    new DocumentationProgressControls(
      rightPanelVisibleStore,
      activityStore,
      progressModelsStore
    )
  );
  const DocumentationProgressComponent = useDocumentationProgressComponent(
    rightPanelVisibleStore,
    activityStore,
    progressModelsStore
  );

  return useMemo(
    () => ({
      setFiles,
      drawings,
      addDrawing,
      changeGallery,
      setGallery: setGalleryState,
      removeFile,
      rotateFile,
      preview: false,
      useDisplayedFiles: useDocumentationDisplayedFiles,
      useMainDisplayedFile: useDocumentationMainDisplayedFile,
      useGalleryState: useDocumentationGalleryState,
      downloadFile,
      refreshPreview,
      openEditComponent: openDialog,
      EditComponent: DocumentationEditDialog,
      editTitleId: CommonTranslationKeys.editDocumentation,
      showActions: documentationGalleryActions,
      ImageDisplayOverlay: DocumentationMapOverlay,
      MiniaturesOverlay: DocumentationMiniaturesOverlay,
      RightPanel: DocumentationProgressComponent,
      rightPanelControls: documentationProgressControls.current,
      skipAutoSlideHandling: true,
      showPdfAsThumbnail: true,
    }),
    [
      setFiles,
      changeGallery,
      removeFile,
      rotateFile,
      downloadFile,
      refreshPreview,
      openDialog,
      DocumentationProgressComponent,
    ]
  );
}
